"use client"

import { useRef, MouseEvent, useState, FormEvent } from "react";
import PopinPortal from "@/lib/react/PopinPortal";
import { Label } from "../shadcn/label";
import { Input } from "../shadcn/input";
import { Button } from "../shadcn/button";
import { Loader2, X } from "lucide-react";
import { createDonation } from "@/app/actions/collections/donations";
import { Textarea } from "../shadcn/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../shadcn/select";

const countryCodes = [
    { code: "+33", country: "France", flag: "🇫🇷" },
    { code: "+32", country: "Belgique", flag: "🇧🇪" },
    { code: "+41", country: "Suisse", flag: "🇨🇭" },
    { code: "+212", country: "Maroc", flag: "🇲🇦" },
    { code: "+216", country: "Tunisie", flag: "🇹🇳" },
    { code: "+213", country: "Algérie", flag: "🇩🇿" },
    { code: "+225", country: "Côte d'Ivoire", flag: "🇨🇮" },
    { code: "+221", country: "Sénégal", flag: "🇸🇳" },
];

/**
 * 
 * A retourner : Un numero avec indicatif
 * Champs :
 * - Indicatif
 * - Numero
 * 
 * Si Numero commence par l'indicatif, on change l'indicatif
 * 
 * Au submit :
 * - Si le numero contient l'indicatif, on envoie le numero
 * - Si le numero n'a pas l'indicatif, on l'ajoute devant
 */

export default function DonationPopin({ isOpen, closeAction }: { isOpen: boolean, closeAction: () => void }) {
    const overlay = useRef(null);
    const [isPending, setIsPending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)
    const [countryCode, setCountryCode] = useState("+221")
    const [phone, setPhone] = useState("")
    const [informations, setInformations] = useState("")
    
    const getCurrentFlag = () => {
        return countryCodes.find(country => country.code === countryCode)?.flag || "🇸🇳"
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const cleanedValue = value.replace(/[^\d+\s-]/g, '');
        
        const detectedCountryCode = countryCodes.find(country => 
            cleanedValue.startsWith(country.code)
        );

        if (detectedCountryCode) {
            setCountryCode(detectedCountryCode.code);
            setPhone(cleanedValue);
        } else {
            setPhone(cleanedValue);
        }
    }

    const handleCountryCodeChange = (newCode: string) => {
        setCountryCode(newCode);
    }

    const getFullPhone = () => {
        if (phone.startsWith(countryCode)) {
            return phone
        }
        return `${countryCode} ${phone}`
    }

    if (!isOpen) return null;

    async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault()
        setIsPending(true)
        setError(null)

        const result = await createDonation({ phone: getFullPhone().replace(/\s/g, ''), informations })
        setIsPending(false)
        setSuccess(true)
    }

    const handleClose = (e: MouseEvent) => {
        if (e.target === overlay.current) {
            closeAction();
        }
    }

    return (
        <PopinPortal>
            <div ref={overlay} onClick={(e) => handleClose(e)} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                { !success ? (
                    <div className="bg-white relative w-full max-w-[560px] py-14 px-4 sm:px-16 rounded-[30px] shadow-lg flex flex-col items-center">
                        <button className="absolute top-6 right-6 cursor-pointer" onClick={closeAction}>
                            <X className="size-6" />
                        </button>
                        <h2 className="text-3xl font-alt text-center md:text-left font-regular mb-4 text-dark">
                            Contactez-nous
                        </h2>
                        <p className="w-full text-center">
                            Laissez-nous votre numéro de téléphone et notre équipe vous recontactera dans les plus brefs délais.
                        </p>
                        <hr className="w-full my-6" />
                        <form className="w-full" onSubmit={onSubmit}>
                            <div className="w-full space-y-2 mb-5">
                                <Label htmlFor="phone">Votre numéro de téléphone :</Label>
                                <div className="flex gap-2">
                                    <Select value={countryCode} onValueChange={handleCountryCodeChange}>
                                        <SelectTrigger className="w-[120px]">
                                            <SelectValue>
                                                <span className="flex items-center gap-2">
                                                    <span className="text-lg">{getCurrentFlag()}</span>
                                                    <span>{countryCode || "Indicatif"}</span>
                                                </span>
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {countryCodes.map((country) => (
                                                <SelectItem key={country.code} value={country.code}>
                                                    <span className="flex items-center gap-2">
                                                        <span className="text-lg">{country.flag}</span>
                                                        <span>{country.code}</span>
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Input 
                                        id="phone" 
                                        name="phone" 
                                        type="tel" 
                                        placeholder="Exemple : +221 77 098 39 44" 
                                        className="flex-1"
                                        required
                                        value={phone}
                                        onChange={handlePhoneChange}
                                    />
                                </div>
                                <Label htmlFor="informations" className="leading-relaxed">
                                    Informations complémentaires sur la donation<br/>(non obligatoire)
                                </Label>
                                <Textarea 
                                    id="informations"
                                    name="informations"
                                    placeholder="Exemple : Je souhaite faire une donation pour une robe de mariage"
                                    value={informations}
                                    onChange={(e) => setInformations(e.target.value)}
                                />
                            </div>
                            <Button 
                                type="submit" 
                                size="xl" 
                                className="w-full mb-3"
                                disabled={isPending}
                            >
                                {isPending && (
                                    <Loader2 className="animate-spin mr-2" />
                                )}
                                Envoyer
                            </Button>
                        </form>
                        {error && (
                            <div className="w-full mb-4 text-red-600 text-center">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="bg-white w-full max-w-[560px] py-14 px-4 sm:px-16 rounded-[30px] shadow-lg flex flex-col items-center">
                        <h2 className="text-3xl font-alt text-center md:text-left font-regular mb-4 text-dark">
                            Merci pour votre don !
                        </h2>
                        <p className="w-full mb-4 text-center">
                            Notre équipe vous recontactera dans les plus brefs délais.
                        </p>
                        <Button 
                            size="xl" 
                            variant="outlineSolid"
                            className="w-full mt-3"
                            onClick={closeAction}
                        >
                            Fermer
                        </Button>
                    </div>
                )}
            </div>
        </PopinPortal>
    );
}